import { useCallback } from "react"
import { toast } from "react-toastify"
import * as yup from "yup"
import { useCompanyControllerSignUpCompanyMutation } from "~/ui-rtk/api/companyApi"
import { domainRegex } from "~/ui-rtk/constants/regex"
import { hasErrorMessage, isBadRequestError } from "~/ui-rtk/utils/http-utils"

export const REVENUE = {
  ZERO_ONE: "ZERO_ONE",
  ONE_TWO: "ONE_TWO",
  TWO_FIVE: "TWO_FIVE",
  FIVE_TEN: "FIVE_TEN",
  TEN_TWENTY: "TEN_TWENTY",
  TWENTY_THIRTY: "TWENTY_THIRTY",
  THIRTY_FORTY: "THIRTY_FORTY",
  FORTY_FIFTY: "FORTY_FIFTY",
  FIFTY_SIXTY: "FIFTY_SIXTY",
  SIXTY_SEVENTY: "SIXTY_SEVENTY",
  SEVENTY_EIGHTY: "SEVENTY_EIGHTY",
  EIGHTY_NINETY: "EIGHTY_NINETY",
  NINETY_HUNDRED: "NINETY_HUNDRED",
  HUNDRED_PLUS: "HUNDRED_PLUS",
}

type TRevenueEstimate = (typeof REVENUE)[keyof typeof REVENUE]
type TRevenueEstimateOption = {
  value: TRevenueEstimate
  label: string
}

export const REVENUE_OPTIONS: TRevenueEstimateOption[] = [
  {
    value: REVENUE.ZERO_ONE,
    label: "< $1M",
  },
  {
    value: REVENUE.ONE_TWO,
    label: "$1M-$2M",
  },
  {
    value: REVENUE.TWO_FIVE,
    label: "$2M-$5M",
  },
  {
    value: REVENUE.FIVE_TEN,
    label: "$5M-$10M",
  },
  {
    value: REVENUE.TEN_TWENTY,
    label: "$10M-$20M",
  },
  {
    value: REVENUE.TWENTY_THIRTY,
    label: "$20M-$30M",
  },
  {
    value: REVENUE.THIRTY_FORTY,
    label: "$30M-$40M",
  },
  {
    value: REVENUE.FORTY_FIFTY,
    label: "$40M-$50M",
  },
  {
    value: REVENUE.FIFTY_SIXTY,
    label: "$50M-$60M",
  },
  {
    value: REVENUE.SIXTY_SEVENTY,
    label: "$60M-$70M",
  },
  {
    value: REVENUE.SEVENTY_EIGHTY,
    label: "$70M-$80M",
  },
  {
    value: REVENUE.EIGHTY_NINETY,
    label: "$80M-$90M",
  },
  {
    value: REVENUE.NINETY_HUNDRED,
    label: "$90M-$100M",
  },
  {
    value: REVENUE.HUNDRED_PLUS,
    label: "> $100M",
  },
]

const REVENUE_KEY_TO_VALUES_MAP: Record<
  TRevenueEstimate,
  {
    from: number
    to: number
  }
> = {
  [REVENUE.ZERO_ONE]: {
    from: 0,
    to: 1_000_000,
  },
  [REVENUE.ONE_TWO]: {
    from: 1_000_000,
    to: 2_000_000,
  },
  [REVENUE.TWO_FIVE]: {
    from: 2_000_000,
    to: 5_000_000,
  },
  [REVENUE.FIVE_TEN]: {
    from: 5_000_000,
    to: 10_000_000,
  },
  [REVENUE.TEN_TWENTY]: {
    from: 10_000_000,
    to: 20_000_000,
  },
  [REVENUE.TWENTY_THIRTY]: {
    from: 20_000_000,
    to: 30_000_000,
  },
  [REVENUE.THIRTY_FORTY]: {
    from: 30_000_000,
    to: 40_000_000,
  },
  [REVENUE.FORTY_FIFTY]: {
    from: 40_000_000,
    to: 50_000_000,
  },
  [REVENUE.FIFTY_SIXTY]: {
    from: 50_000_000,
    to: 60_000_000,
  },
  [REVENUE.SIXTY_SEVENTY]: {
    from: 60_000_000,
    to: 70_000_000,
  },
  [REVENUE.SEVENTY_EIGHTY]: {
    from: 70_000_000,
    to: 80_000_000,
  },
  [REVENUE.EIGHTY_NINETY]: {
    from: 80_000_000,
    to: 90_000_000,
  },
  [REVENUE.NINETY_HUNDRED]: {
    from: 90_000_000,
    to: 100_000_000,
  },
  [REVENUE.HUNDRED_PLUS]: {
    from: 100_000_000,
    to: Number.MAX_SAFE_INTEGER,
  },
}

export type AccountDetailsFormValues = {
  domain: string
  companyName: string
  revenueEstimate?: TRevenueEstimate
}

const initialValues: AccountDetailsFormValues = {
  domain: "",
  companyName: "",
}

export const validationSchema = yup.object().shape({
  domain: yup
    .string()
    .transform(value => value.trim())
    .matches(domainRegex, { message: "Invalid domain format" })
    .required("Domain is required"),
  companyName: yup.string().required("Company name is required!"),
  revenueEstimate: yup
    .string()
    .required("Revenue must be set")
    .oneOf(Object.values(REVENUE)),
})

export const useConnect = () => {
  const [signUpCompanyAsync, { isLoading }] =
    useCompanyControllerSignUpCompanyMutation()

  const handleSuccess = () => {
    toast.success(`Company was successfully created`, {
      toastId: "onboarding-account-details-success",
    })
    window.location.reload()
  }

  const submit = useCallback(async (values: AccountDetailsFormValues) => {
    try {
      const { domain, companyName, revenueEstimate } = values
      if (!revenueEstimate) {
        return
      }
      await signUpCompanyAsync({
        signUpCompanyDto: {
          domain,
          companyName,
          metadata: {
            setup: {
              revenueEstimate: REVENUE_KEY_TO_VALUES_MAP[revenueEstimate],
            },
          },
        },
      }).unwrap()

      handleSuccess()
    } catch (error) {
      if (hasErrorMessage(error)) {
        const message = error.data.message
        if (message.includes("setup external integrations")) {
          return handleSuccess()
        }

        if (isBadRequestError(error)) {
          toast.error(error.data.message, {
            toastId: "onboarding-account-details-bad-request-error",
          })
          return
        }
      } else {
        toast.error("Something went wrong", {
          toastId: "onboarding-account-details-unexpected-error",
        })
      }
    }
  }, [])

  return {
    isSubmitting: isLoading,
    initialValues,
    validationSchema,
    updateAccountDetails: submit,
  }
}
